import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import PageShell from '../../../components/layout/PageShell';
import PageHeader from '../components/PageHeader';
import ConfirmationDialog from '../components/ConfirmationDialog';
import StatusChip from '../components/StatusChip';
import { safeAdminError } from '../components/adminPresentation';
import {
  fetchRegistrationSettings,
  updateRegistrationSettings,
} from '../services/superAdminService';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const SuperAdminSettingsPage = () => {
  const { token } = useAuth();
  const [registrationsEnabled, setRegistrationsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [pendingValue, setPendingValue] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Sign in as a super admin to manage settings.');
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const loadSettings = async () => {
      setIsLoading(true);
      setError('');
      try {
        const payload = await fetchRegistrationSettings(token);
        if (!isMounted) return;
        setRegistrationsEnabled(Boolean(payload?.enabled));
      } catch (loadError) {
        if (!isMounted) return;
        setError(safeAdminError(loadError, 'Unable to load settings.'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const saveSetting = async () => {
    const nextValue = pendingValue;
    const previousValue = registrationsEnabled;

    setRegistrationsEnabled(nextValue);
    setIsSaving(true);
    setError('');

    try {
      const payload = await updateRegistrationSettings({ enabled: nextValue, token });
      setRegistrationsEnabled(Boolean(payload?.enabled));
      setToastMessage('Registration setting updated successfully.');
    } catch (saveError) {
      setRegistrationsEnabled(previousValue);
      setError(safeAdminError(saveError, 'Unable to update this setting.'));
    } finally {
      setIsSaving(false);
      setPendingValue(null);
    }
  };

  return (
    <PageShell containerClassName="p-0">
    <PageHeader title="Platform Settings" description="Manage high-impact configuration for the CrickArena platform." />
    <section className="sa-panel">
      <header className="sa-panel__header"><div><h2>User registrations</h2><p>Control access to the public account-registration flow.</p></div>{!isLoading ? <StatusChip value={registrationsEnabled ? 'Active' : 'Disabled'} /> : null}</header>
      <div className="sa-settings-list">
        <div className="sa-setting-row"><div><strong>Allow new user registrations</strong><p>When disabled, existing users can still sign in and use their accounts. New public account creation is unavailable.</p></div>

        {isLoading ? (
          <div className="placeholder-glow" aria-hidden="true">
            <span className="placeholder col-4" />
          </div>
        ) : (
          <div className="form-check form-switch">
            <input
              id="saRegistrationToggle"
              className="form-check-input"
              type="checkbox"
              checked={registrationsEnabled}
              onChange={(event) => setPendingValue(event.target.checked)}
              disabled={isSaving}
            />
            <label className="form-check-label" htmlFor="saRegistrationToggle">{registrationsEnabled ? 'Enabled' : 'Disabled'}</label>
          </div>
        )}
        </div>

        {isSaving && <p className="small text-muted mb-0">Saving changes…</p>}
        {error && <p className="small text-danger mb-0">{error}</p>}
      </div>

      {toastMessage && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1100 }}>
          <div className="toast show align-items-center text-bg-success border-0" role="status" aria-live="polite">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                aria-label="Close"
                onClick={() => setToastMessage('')}
              />
            </div>
          </div>
        </div>
      )}
    </section>
    <ConfirmationDialog open={pendingValue !== null} title={`${pendingValue ? 'Enable' : 'Disable'} new user registrations?`} entityName="Public registration" consequence={pendingValue ? 'New visitors will be able to create CrickArena accounts.' : 'New visitors will be unable to create accounts until this setting is enabled again.'} unaffected="Existing accounts, authentication sessions, tournaments, scoring, and stored data are unchanged." confirmLabel={pendingValue ? 'Enable registrations' : 'Disable registrations'} tone={pendingValue ? 'primary' : 'danger'} busy={isSaving} onCancel={() => setPendingValue(null)} onConfirm={saveSetting} />
  
    </PageShell>
  );
};

export default SuperAdminSettingsPage;
